import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText, BarChart2, Wrench, Award, Package, BookOpen,
  FlaskConical, Settings, Download, Clock, CheckCircle2,
  AlertCircle, Loader2, RefreshCw, Plus,
} from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import { TableSkeleton } from '@/components/ui/LoadingSkeleton';
import api from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type ReportFormat = 'PDF' | 'Excel' | 'CSV';
type ReportStatus = 'ready' | 'generating' | 'failed';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  lastGenerated: string | null;
  custom?: boolean;
}

interface GeneratedReport {
  id: string;
  name: string;
  type: string;
  generatedBy: string;
  date: string;
  format: ReportFormat;
  status: ReportStatus;
  downloadUrl?: string;
}

interface GenerateForm {
  templateId: string;
  templateName: string;
  dateFrom: string;
  dateTo: string;
  labs: string[];
  format: ReportFormat;
  modules: string[];
  filters: Record<string, string>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TEMPLATES: ReportTemplate[] = [
  { id: 'capa-summary', name: 'CAPA Summary Report', description: 'Open, in-progress, and closed corrective actions with root cause analysis', icon: AlertCircle, color: '#DC2626', lastGenerated: '2026-03-28' },
  { id: 'audit-findings', name: 'Audit Findings Report', description: 'Internal and external audit findings, observations, and follow-up status', icon: FileText, color: '#2563EB', lastGenerated: '2026-03-15' },
  { id: 'equipment-calibration', name: 'Equipment Calibration Report', description: 'Calibration schedules, overdue equipment, and certification status', icon: Wrench, color: '#D97706', lastGenerated: '2026-04-01' },
  { id: 'certification-status', name: 'Certification Status Report', description: 'Active, expiring, and expired certifications by lab and partner', icon: Award, color: '#16A34A', lastGenerated: '2026-04-02' },
  { id: 'batch-quality', name: 'Batch Quality Report', description: 'Batch release status, QC hold items, and quality metrics by batch', icon: Package, color: '#7C3AED', lastGenerated: '2026-03-30' },
  { id: 'sop-compliance', name: 'SOP Compliance Report', description: 'SOP adherence rates, overdue reviews, and version control status', icon: BookOpen, color: '#b07d1a', lastGenerated: '2026-03-22' },
  { id: 'lab-performance', name: 'Lab Performance Report', description: 'First-pass yield, turnaround times, and defect rates by lab', icon: BarChart2, color: '#b07d1a', lastGenerated: '2026-04-01' },
  { id: 'custom', name: 'Custom Report', description: 'Build a tailored report by selecting specific modules and filters', icon: Settings, color: '#64748b', lastGenerated: null, custom: true },
];

const ALL_LABS = ['Lab A', 'Lab B', 'Lab C', 'Lab D', 'Lab E'];
const ALL_MODULES = ['CAPA', 'Audits', 'Equipment', 'Certifications', 'SOPs', 'Batches', 'Requests'];
const FORMAT_OPTIONS: ReportFormat[] = ['PDF', 'Excel', 'CSV'];

const MOCK_REPORTS: GeneratedReport[] = [
  { id: 'r1', name: 'CAPA Summary — Q1 2026', type: 'CAPA Summary Report', generatedBy: 'Priya Sharma', date: '2026-04-03 10:15', format: 'PDF', status: 'ready', downloadUrl: '#' },
  { id: 'r2', name: 'Lab Performance — Mar 2026', type: 'Lab Performance Report', generatedBy: 'Rajan Mehta', date: '2026-04-01 16:30', format: 'Excel', status: 'ready', downloadUrl: '#' },
  { id: 'r3', name: 'Certification Status — All Labs', type: 'Certification Status Report', generatedBy: 'Ananya Iyer', date: '2026-04-02 09:00', format: 'PDF', status: 'generating' },
  { id: 'r4', name: 'Audit Findings — Internal', type: 'Audit Findings Report', generatedBy: 'Priya Sharma', date: '2026-03-28 14:20', format: 'PDF', status: 'ready', downloadUrl: '#' },
  { id: 'r5', name: 'SOP Compliance — Lab B', type: 'SOP Compliance Report', generatedBy: 'Kiran Das', date: '2026-03-25 11:45', format: 'CSV', status: 'failed' },
];

// ─── Status helpers ───────────────────────────────────────────────────────────

function ReportStatusIcon({ status }: { status: ReportStatus }) {
  if (status === 'ready') return <CheckCircle2 size={14} className="text-green-600" />;
  if (status === 'generating') return <Loader2 size={14} className="text-blue-500 animate-spin" />;
  return <AlertCircle size={14} className="text-red-500" />;
}

function statusBadgeVariant(s: ReportStatus): string {
  if (s === 'ready') return 'completed';
  if (s === 'generating') return 'in_progress';
  return 'overdue';
}

function statusLabel(s: ReportStatus): string {
  if (s === 'ready') return 'Ready';
  if (s === 'generating') return 'Generating';
  return 'Failed';
}

// ─── Default form ─────────────────────────────────────────────────────────────

function defaultForm(template: ReportTemplate): GenerateForm {
  const today = new Date().toISOString().slice(0, 10);
  const threeMonths = new Date(Date.now() - 90 * 86400_000).toISOString().slice(0, 10);
  return {
    templateId: template.id,
    templateName: template.name,
    dateFrom: threeMonths,
    dateTo: today,
    labs: [],
    format: 'PDF',
    modules: template.custom ? [] : [],
    filters: {},
  };
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ReportBuilder() {
  const queryClient = useQueryClient();
  const [activeTemplate, setActiveTemplate] = useState<ReportTemplate | null>(null);
  const [form, setForm] = useState<GenerateForm | null>(null);
  const [successReport, setSuccessReport] = useState<{ name: string; url?: string } | null>(null);

  const { data, isLoading } = useQuery<{ reports: GeneratedReport[] }>({
    queryKey: ['reports'],
    queryFn: () => api.get('/reports').then((r) => r.data),
    staleTime: 60_000,
  });

  const reports: GeneratedReport[] = data?.reports ?? MOCK_REPORTS;

  const generateMutation = useMutation({
    mutationFn: (payload: GenerateForm) => api.post('/reports/generate', payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      setSuccessReport({ name: form?.templateName ?? 'Report', url: res.data?.downloadUrl });
      setActiveTemplate(null);
      setForm(null);
    },
  });

  const handleGenerate = (template: ReportTemplate) => {
    setActiveTemplate(template);
    setForm(defaultForm(template));
    setSuccessReport(null);
  };

  const toggleLab = (lab: string) => {
    if (!form) return;
    setForm((f) => f ? ({
      ...f,
      labs: f.labs.includes(lab) ? f.labs.filter((l) => l !== lab) : [...f.labs, lab],
    }) : f);
  };

  const toggleModule = (mod: string) => {
    if (!form) return;
    setForm((f) => f ? ({
      ...f,
      modules: f.modules.includes(mod) ? f.modules.filter((m) => m !== mod) : [...f.modules, mod],
    }) : f);
  };

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Report Builder"
        subtitle="Generate GMP · Audit · Compliance reports"
        actions={
          <button
            onClick={() => handleGenerate(TEMPLATES[TEMPLATES.length - 1])}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <Plus size={14} /> Custom Report
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT — Templates */}
        <div className="space-y-3">
          <h2 className="font-serif font-semibold text-sm text-text-muted uppercase tracking-wide">Report Templates</h2>
          <div className="space-y-3">
            {TEMPLATES.map((tpl) => {
              const Icon = tpl.icon;
              return (
                <div key={tpl.id} className="card p-4 flex items-start gap-4 hover:shadow-md transition-shadow">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${tpl.color}18` }}
                  >
                    <Icon size={18} style={{ color: tpl.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-text">{tpl.name}</p>
                    <p className="text-xs text-text-muted mt-0.5 leading-relaxed">{tpl.description}</p>
                    {tpl.lastGenerated && (
                      <p className="text-xs text-text-muted mt-1 flex items-center gap-1">
                        <Clock size={10} /> Last generated: {tpl.lastGenerated}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleGenerate(tpl)}
                    className="btn-primary text-xs px-3 py-1.5 flex-shrink-0"
                  >
                    Generate
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — Recent Reports */}
        <div className="space-y-3">
          <h2 className="font-serif font-semibold text-sm text-text-muted uppercase tracking-wide">Recent Reports</h2>
          {isLoading ? (
            <TableSkeleton rows={5} />
          ) : reports.length === 0 ? (
            <div className="card">
              <EmptyState icon="file" title="No reports yet" description="Generate your first report using the templates on the left." />
            </div>
          ) : (
            <div className="card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-bg border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-text-muted">Report</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-text-muted hidden md:table-cell">By</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-text-muted hidden md:table-cell">Format</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-text-muted">Status</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r, i) => (
                    <tr key={r.id} className={`border-b border-border last:border-0 hover:bg-bg/50 ${i % 2 === 0 ? '' : 'bg-bg/30'}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-text text-sm leading-tight">{r.name}</p>
                        <p className="text-xs text-text-muted mt-0.5">{r.date}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs text-text-muted">{r.generatedBy}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-bg border border-border text-text-muted">{r.format}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <ReportStatusIcon status={r.status} />
                          <PillBadge value={statusBadgeVariant(r.status)} label={statusLabel(r.status)} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {r.status === 'ready' && r.downloadUrl ? (
                          <button
                            onClick={() => window.open(r.downloadUrl, '_blank')}
                            className="btn-ghost text-xs flex items-center gap-1"
                          >
                            <Download size={12} /> Download
                          </button>
                        ) : r.status === 'failed' ? (
                          <button className="btn-ghost text-xs flex items-center gap-1 text-red-500">
                            <RefreshCw size={12} /> Retry
                          </button>
                        ) : (
                          <span className="text-xs text-text-muted">Processing…</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Generate Modal */}
      {activeTemplate && form && (
        <Modal
          isOpen={!!activeTemplate}
          onClose={() => { setActiveTemplate(null); setForm(null); }}
          title={`Generate: ${activeTemplate.name}`}
          subtitle="Configure parameters before generating"
          size="lg"
          footer={
            <>
              <button onClick={() => { setActiveTemplate(null); setForm(null); }} className="btn-ghost text-sm">Cancel</button>
              <button
                onClick={() => generateMutation.mutate(form)}
                disabled={generateMutation.isPending}
                className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {generateMutation.isPending ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : 'Generate Report'}
              </button>
            </>
          }
        >
          <div className="space-y-5">
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-text-muted mb-1">From Date *</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bg text-text"
                  value={form.dateFrom}
                  onChange={(e) => setForm((f) => f ? { ...f, dateFrom: e.target.value } : f)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-text-muted mb-1">To Date *</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bg text-text"
                  value={form.dateTo}
                  onChange={(e) => setForm((f) => f ? { ...f, dateTo: e.target.value } : f)}
                />
              </div>
            </div>

            {/* Labs */}
            <div>
              <label className="block text-xs font-semibold text-text-muted mb-2">Labs (leave empty for all)</label>
              <div className="grid grid-cols-3 gap-2">
                {ALL_LABS.map((lab) => (
                  <label key={lab} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.labs.includes(lab)}
                      onChange={() => toggleLab(lab)}
                      className="accent-teal-600"
                    />
                    <span className="text-sm text-text">{lab}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Modules (Custom only) */}
            {activeTemplate.custom && (
              <div>
                <label className="block text-xs font-semibold text-text-muted mb-2">Modules to Include *</label>
                <div className="grid grid-cols-3 gap-2">
                  {ALL_MODULES.map((mod) => (
                    <label key={mod} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.modules.includes(mod)}
                        onChange={() => toggleModule(mod)}
                        className="accent-teal-600"
                      />
                      <span className="text-sm text-text">{mod}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Format */}
            <div>
              <label className="block text-xs font-semibold text-text-muted mb-2">Output Format</label>
              <div className="flex gap-3">
                {FORMAT_OPTIONS.map((fmt) => (
                  <label key={fmt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="format"
                      value={fmt}
                      checked={form.format === fmt}
                      onChange={() => setForm((f) => f ? { ...f, format: fmt } : f)}
                      className="accent-teal-600"
                    />
                    <span className="text-sm text-text font-medium">{fmt}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Error */}
            {generateMutation.isError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                Failed to generate report. Please try again.
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Success download modal */}
      {successReport && (
        <Modal
          isOpen={!!successReport}
          onClose={() => setSuccessReport(null)}
          title="Report Ready"
          subtitle={successReport.name}
          size="sm"
          footer={
            <button onClick={() => setSuccessReport(null)} className="btn-ghost text-sm">Close</button>
          }
        >
          <div className="text-center py-4 space-y-3">
            <CheckCircle2 size={40} className="text-green-600 mx-auto" />
            <p className="text-sm text-text">Your report has been generated successfully.</p>
            {successReport.url && (
              <button
                onClick={() => window.open(successReport.url, '_blank')}
                className="btn-primary flex items-center gap-2 mx-auto text-sm"
              >
                <Download size={14} /> Download Report
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
